import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Row, Col, Collapse, Badge, Card, Modal, Table } from "react-bootstrap";
import { FiChevronDown } from "react-icons/fi";
import { FormControlLabel, Checkbox, Radio, RadioGroup } from "@mui/material";
import { Subtask, Subject, Topic, Subtopic, DeleteTarget, ContentItem, SubtopicContent } from "./ProjectConfigurationTypes.ts";
import { SUBTASK_TYPES, SubtaskTypes, CODING_EDITOR_TYPES } from "./projectConfigConstants.ts";
import { ContentTypeConstants } from "../constants/contentCreationConstants";
import { apiGet, apiPost } from "../utils/apiAuth";
import { getTopicsSubtopicsBySubject } from "../utils/topicsSubtopicsCache";
import { PiMonitorPlayBold } from "react-icons/pi";
import { SlNotebook } from "react-icons/sl";
import { BsListTask } from "react-icons/bs";
import { LiaLaptopCodeSolid } from "react-icons/lia";
import {FaCode} from "react-icons/fa";
type PreviewContent =
| { type: "video"; otp: string; playback_info: string }
| { type: "notes"; content: string };

/**
 * Content Status Type Definition
 */
type ContentStatus = {
  video?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  notes?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  mcq?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  project_coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
};

/**
 * Subtopic Info Table Component
 * Displays subject, topic, subtopic, type, and editor in a single table
 */
const SubtopicInfoTable: React.FC<{
  subtopicInfo: { subject_name?: string; topic_name?: string; subtopic_name?: string } | null;
  typeLabel?: string;
  editorLabel?: string;
}> = ({ subtopicInfo, typeLabel, editorLabel }) => {
  const showType = typeLabel != null && typeLabel !== "";
  const showEditor = editorLabel != null && editorLabel !== "";
  if (!subtopicInfo && !showType && !showEditor) return null;

  return (
    <Table bordered size="sm" className="mb-3">
      <thead>
        <tr>
          <th>Subject</th>
          <th>Topic</th>
          <th>Sub Topic</th>
          {showType && <th>Type</th>}
          {showEditor && <th>Editor</th>}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{subtopicInfo?.subject_name ?? "—"}</td>
          <td>{subtopicInfo?.topic_name ?? "—"}</td>
          <td>{subtopicInfo?.subtopic_name ?? "—"}</td>
          {showType && <td>{typeLabel ?? "—"}</td>}
          {showEditor && <td>{editorLabel ?? "—"}</td>}
        </tr>
      </tbody>
    </Table>
  );
};

/**
 * Content Status Indicator Component
 * Displays a button with approval status text (Assigned, Uploaded, or Approved)
 */
const ContentStatusIndicator: React.FC<{
  contentId: string;
  contentType: "video" | "notes" | "mcq" | "coding" | "project_coding";
  contentStatus?: ContentStatus | null;
  onStatusClick?: (contentId: string, contentType: string, statusKey: string) => void;
}> = ({ contentId, contentType, contentStatus, onStatusClick }) => {
  if (!contentStatus) return null;
  
  const typeStatus = contentStatus[contentType];
  if (!typeStatus) return null;
  
  const normalizedContentId = String(contentId).trim();
  const statusConfig = [
    { key: "assigned", text: "Assigned", color: "#dc3545" },
    { key: "uploaded", text: "Uploaded", color: "#ffc107" },
    { key: "approved", text: "Approved", color: "#28a745" }
  ];
  
  // Find the highest priority status (approved > uploaded > assigned)
  const priorityOrder = ["approved", "uploaded", "assigned"];
  let activeStatus: { key: string; text: string; color: string } | null = null;
  
  for (const statusKey of priorityOrder) {
    const status = statusConfig.find(s => s.key === statusKey);
    if (status && typeStatus[status.key as keyof typeof typeStatus]?.some(
      id => String(id).trim() === normalizedContentId
    )) {
      activeStatus = status;
      break;
    }
  }
  
  if (!activeStatus) return null;
  
  return (
    <div className="d-flex align-items-center justify-content-center ms-2" style={{ minHeight: "100%" }}>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => onStatusClick && onStatusClick(contentId, contentType, activeStatus!.key)}
        disabled
        style={{
          borderColor: activeStatus.color,
          color: activeStatus.color,
          fontSize: "12px",
          padding: "2px 8px",
          minWidth: "70px"
        }}
      >
        {activeStatus.text}
      </Button>
    </div>
  );
};

  // Helper function to get content type icon
  const getSubtaskTypeIcon = (
    subtaskType: SubtaskTypes
  ): React.ReactElement => {
    switch (subtaskType) {
      case "video":
        return <PiMonitorPlayBold size={20} className="text-primary me-2"/>;
      case "notes":
        return <SlNotebook size={20} className="text-primary me-2"/>;
      case "notebook":
        return <SlNotebook size={20} className="text-primary me-2"/>;
      case "mcq":
        return <BsListTask size={20} className="text-primary me-2"/>;
      case "coding":
        return <LiaLaptopCodeSolid size={20} className="text-primary me-2"/>;
      case "project_coding":
        return <FaCode size={20} className="text-primary me-2"/>;
      default:
        return <></>;
    }
  };

/**
 * Gets all question IDs from level1, level2, and level3 arrays
 * @param subtask - The subtask object containing level arrays
 * @returns Array of all question IDs as strings
 */
const getAllQuestionIds = (subtask: any): string[] => {
  const allIds: string[] = [];
  const l1 = subtask.level1 || [];
  const l2 = subtask.level2 || [];
  const l3 = subtask.level3 || [];
  if (Array.isArray(l1)) allIds.push(...l1.map((id: any) => String(id)));
  if (Array.isArray(l2)) allIds.push(...l2.map((id: any) => String(id)));
  if (Array.isArray(l3)) allIds.push(...l3.map((id: any) => String(id)));
  return allIds;
};

/**
 * Level Display Component - Renders MCQ/Coding questions for a specific level
 */
const LevelQuestionsDisplay: React.FC<{
  levelKey: string;
  questions: any[];
  subtask: any;
  contentStatus?: ContentStatus;
  onStatusClick?: (contentId: string, contentType: string, statusKey: string) => void;
}> = ({ levelKey, questions, subtask, contentStatus, onStatusClick }) => {
  const allIds = getAllQuestionIds(subtask);
  const contentType = (subtask.subtaskType === "mcq" ? "mcq" : subtask.subtaskType === "coding" ? "coding" : "mcq") as "video" | "notes" | "mcq" | "coding" | "project_coding";

  return (
    <>
      {questions.map((question: any) => {
        const itemId = String(question.content_id);
        const isChecked = allIds.includes(itemId);

        return (
          <div key={question.content_id} className="d-flex mb-2 align-items-center">
            <div className="d-flex align-items-start p-2 border rounded flex-grow-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    disabled
                  />
                }
                label={
                  <div style={{ marginLeft: "8px", fontSize: "12px", marginTop: "4px", color: "#000" }}>
                    {question.title || question.content_id}
                  </div>
                }
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "#000 !important"
                  }
                }}
              />
            </div>
            <ContentStatusIndicator
              contentId={itemId}
              contentType={contentType}
              contentStatus={contentStatus}
              onStatusClick={onStatusClick}
            />
          </div>
        );
      })}
    </>
  );
};

/**
 * MCQ/Coding Level Display - Renders content IDs for a specific level
 */
const McqCodingLevelDisplay: React.FC<{
  levelKey: string;
  levelData: any[];
  contentType?: "video" | "notes" | "mcq" | "coding" | "project_coding";
  contentStatus?: ContentStatus | null;
  onStatusClick?: (contentId: string, contentType: string, statusKey: string) => void;
}> = ({ levelKey, levelData, contentType, contentStatus, onStatusClick }) => {
  if (!Array.isArray(levelData) || levelData.length === 0) return null;

  return (
    <>
      {levelData.map((qid: string, idx: number) => {
        const contentId = String(qid);
        return (
          <div key={`${levelKey}-${idx}`} className="d-flex mb-2 align-items-center">
            <div className="p-2 border rounded flex-grow-1">
              <strong>{levelKey}</strong> - {contentId}
            </div>
            {contentType && (
              <ContentStatusIndicator
                contentId={contentId}
                contentType={contentType}
                contentStatus={contentStatus}
                onStatusClick={onStatusClick}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

interface SubtaskComponentProps {
  subtask: Subtask;
  phaseIndex: number;
  partIndex: number;
  taskIndex: number;
  subtaskIndex: number;
  subtaskId: string;
  taskId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: Subject[];
  availableTopics: Topic[];
  availableSubtopics: Subtopic[];
  contentStatus?: ContentStatus | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onPreviewContent: (type: "video" | "notes", content: PreviewContent) => void;
  onFetchTopics?: (subjectId: string) => Promise<void>;
}

const SubtaskComponent: React.FC<SubtaskComponentProps> = ({
  subtask,
  phaseIndex,
  partIndex,
  taskIndex,
  subtaskIndex,
  subtaskId,
  taskId,
  isExpanded,
  projectId,
  subjects,
  availableTopics: availableTopicsProp,
  availableSubtopics: availableSubtopicsProp,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onPreviewContent
}) => {
  const navigate = useNavigate();

  const handleStatusClick = (contentId: string, contentType: string, statusKey: string) => {
    try {
      // store approval status and batchAssigned in sessionStorage like ProjectContentBank
      sessionStorage.setItem('contentCreation_approvalStatus', String(statusKey));
      sessionStorage.setItem('contentCreation_batchAssigned', String(false));

      // store freezeFilters and project/subject/topic data in sessionStorage
      sessionStorage.setItem('contentCreation_freezeFilters', 'true');
      sessionStorage.setItem('contentCreation_projectId', JSON.stringify(projectId));
      sessionStorage.setItem('contentCreation_isAssignedContent', 'true');
      sessionStorage.setItem('contentCreation_contentEntityType', JSON.stringify(contentType));
      sessionStorage.setItem('contentCreation_projectIdForStatusUpdate', JSON.stringify(projectId));

      const subjectObj = subjects.find(s => s.subject_id === subtask.subjectId);
      const subjectName = subjectObj?.subject_name || "";
      if (subtask.subjectId) sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(subtask.subjectId));
      if (subjectName) sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      if (subtask.topicId) sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(subtask.topicId));
      if (subtask.subtopicId) sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(subtask.subtopicId));

      // map content type to values expected by content creation page
      const contentTypeMap: Record<string, string> = {
        video: ContentTypeConstants.VIDEO.key,
        notes: ContentTypeConstants.NOTES.key,
        notebook: ContentTypeConstants.NOTEBOOK.key,
        mcq: ContentTypeConstants.MCQ.key,
        coding: ContentTypeConstants.CODING.key,
        project_coding: ContentTypeConstants.PROJECT_CODING.key
      };
      const mappedType = contentTypeMap[contentType] || contentType;
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(mappedType));

      // set selected entity id depending on content type
      if (contentType === ContentTypeConstants.VIDEO.key) {
        sessionStorage.setItem('contentCreation_selectedVideo', JSON.stringify(String(contentId)));
      } else if (contentType === ContentTypeConstants.NOTES.key) {
        sessionStorage.setItem('contentCreation_selectedNote', JSON.stringify(String(contentId)));
      } else if (contentType === ContentTypeConstants.NOTEBOOK.key) {
        sessionStorage.setItem('contentCreation_selectedNotebook', JSON.stringify(String(contentId)));
      } else if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
        sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(String(contentId)));
      }

      // navigate to content creation with same data shape as ProjectContentBank
      navigate('/content-creation', {
        state: {
          data: {
            freezeFilters: true,
            subject_id: subtask.subjectId || "",
            subject_name: subjectName,
            topic_id: subtask.topicId || "",
            sub_topic_id: subtask.subtopicId || "",
            content_type: mappedType,  // Include content_type for initialization
            project_id: projectId,
            contentId: contentId
          }
        }
      });
    } catch (e) {
      console.error('Error navigating to content-creation:', e);
    }
  };
  // Ensure availableTopics and availableSubtopics are always arrays
  const availableTopics = Array.isArray(availableTopicsProp) ? availableTopicsProp : [];
  const availableSubtopics = Array.isArray(availableSubtopicsProp) ? availableSubtopicsProp : [];
  const [loadingContent, setLoadingContent] = useState(false);
  const [mcqCodingContent, setMcqCodingContent] = useState<any>(null);
  const lastFetchedRef = useRef<{ subtopicId: string; subtaskType: string } | null>(null);
  
  // State for assign mode level counts (mcq/coding only)
  const [level1Count, setLevel1Count] = useState<string>("0");
  const [level2Count, setLevel2Count] = useState<string>("0");
  const [level3Count, setLevel3Count] = useState<string>("0");

  // View-only: no edit mode
  const isMcqCoding = subtask.subtaskType === "mcq" || subtask.subtaskType === "coding";
  const isVideoOrNotes =
    subtask.subtaskType === "video" ||
    subtask.subtaskType === "notes" ||
    subtask.subtaskType === "notebook";
  
  /**
   * Calculates the total number of content items across all levels (level1, level2, level3)
   * For videos/notes, also checks selectedContentId
   * @returns Total count of content items in all level arrays
   */
  const getContentLength = () => {
    const l1 = (subtask as any).level1 || [];
    const l2 = (subtask as any).level2 || [];
    const l3 = (subtask as any).level3 || [];
    const levelCount = (Array.isArray(l1) ? l1.length : 0) + 
           (Array.isArray(l2) ? l2.length : 0) + 
           (Array.isArray(l3) ? l3.length : 0);
    
    // For videos/notes, also check if selectedContentId exists
    if (isVideoOrNotes && subtask.selectedContentId) {
      return 1; // Has content
    }
    
    return levelCount;
  };
  const hasExistingContent = getContentLength() > 0;

  // State for view mode data (view-only; no edit mode)
  const [subtopicInfo, setSubtopicInfo] = useState<{ subtopic_name?: string; topic_name?: string; subject_name?: string } | null>(null);
  const [viewModeContent, setViewModeContent] = useState<any>(null);
  const [loadingViewData, setLoadingViewData] = useState(false);

  // Synchronous fallback: populate subtopicInfo immediately from props/sessionStorage
  useEffect(() => {
    if (!subtask || !subtask.subjectId) {
      setSubtopicInfo(null);
      return;
    }

    let sName = "";
    // Priority 1: Find subject from subjects prop array
    // Check if subjects is a valid array with elements
    if (Array.isArray(subjects) && subjects.length > 0) {
      const subject = subjects.find(s => {
        const subjectIdStr = String(s.subject_id || '').trim();
        const subtaskSubjectIdStr = String(subtask.subjectId || '').trim();
        return subjectIdStr === subtaskSubjectIdStr;
      });
      if (subject && subject.subject_name) {
        sName = String(subject.subject_name).trim();
      }
    }
    
    // Priority 2: Try sessionStorage if still not found
    if (!sName) {
      try {
        const sess = sessionStorage.getItem('contentCreation_subjectName');
        if (sess) {
          const parsed = JSON.parse(sess);
          if (parsed && typeof parsed === 'string') {
            sName = parsed.trim();
          }
        }
      } catch (e) {
        // ignore
      }
    }

    let tName = "";
    let stName = "";
    
    // Get topic name from availableTopics
    if (subtask.topicId && Array.isArray(availableTopics) && availableTopics.length > 0) {
      const topic = availableTopics.find(t => {
        const topicIdStr = String(t.topic_id || '').trim();
        const subtaskTopicIdStr = String(subtask.topicId || '').trim();
        return topicIdStr === subtaskTopicIdStr;
      });
      if (topic && topic.topic_name) {
        tName = String(topic.topic_name).trim();
        // Get subtopic name from topic's sub_topics
        if (Array.isArray(topic.sub_topics) && topic.sub_topics.length > 0 && subtask.subtopicId) {
          const st = topic.sub_topics.find((x: any) => {
            const stIdStr = String(x.subtopic_id || '').trim();
            const subtaskSubtopicIdStr = String(subtask.subtopicId || '').trim();
            return stIdStr === subtaskSubtopicIdStr;
          });
          if (st && st.subtopic_name) {
            stName = String(st.subtopic_name).trim();
          }
        }
      }
    }

    // Fallback: Get subtopic name from availableSubtopics if not found yet
    if (!stName && subtask.subtopicId && Array.isArray(availableSubtopics) && availableSubtopics.length > 0) {
      const st = availableSubtopics.find(x => {
        const stIdStr = String(x.subtopic_id || '').trim();
        const subtaskSubtopicIdStr = String(subtask.subtopicId || '').trim();
        return stIdStr === subtaskSubtopicIdStr;
      });
      if (st && st.subtopic_name) {
        stName = String(st.subtopic_name).trim();
      }
    }

    // Update subtopicInfo even if only partial info is available
    // This ensures the table shows whatever data we have
    if (sName || tName || stName) {
      setSubtopicInfo({ 
        subject_name: sName || undefined, 
        topic_name: tName || undefined, 
        subtopic_name: stName || undefined 
      });
    } else {
      // If we have no data, set to null so table doesn't render with all "--"
      setSubtopicInfo(null);
    }
  }, [subtask?.subjectId, subtask?.topicId, subtask?.subtopicId, subjects, availableTopics, availableSubtopics]);

  /**
   * Fetches subtopic information and content list for view mode display
   * Step 1: Get subject name from subjects array
   * Step 2: Fetch topics and subtopics from API using subjectId
   * Step 3: Find topic and subtopic names from the fetched data
   * Step 4: For MCQ/Coding: Collect all question IDs from level1, level2, and level3 arrays
   * Step 5: For MCQ/Coding: Fetch content list using the collected question IDs
   * Step 6: Store the fetched data in state for display
   */
  const fetchViewModeData = async () => {
    if (!subtask.subtopicId || !subtask.subjectId) return;
    
    try {
      setLoadingViewData(true);
      
      // Step 1: Get subject name from subjects array, fall back to sessionStorage
      let subjectName = "";
      if (Array.isArray(subjects) && subjects.length > 0) {
        const subject = subjects.find(s => {
          const subjectIdStr = String(s.subject_id || '').trim();
          const subtaskSubjectIdStr = String(subtask.subjectId || '').trim();
          return subjectIdStr === subtaskSubjectIdStr;
        });
        if (subject && subject.subject_name) {
          subjectName = String(subject.subject_name).trim();
        }
      }
      
      if (!subjectName) {
        try {
          const sess = sessionStorage.getItem('contentCreation_subjectName');
          if (sess) {
            const parsed = JSON.parse(sess);
            if (parsed && typeof parsed === 'string') {
              subjectName = parsed.trim();
            }
          }
        } catch (e) {
          // ignore
        }
      }
      
      // Step 2: Check sessionStorage first, then fetch topics and subtopics from API if needed
      let topicName = "";
      let subtopicName = "";
      
      let topics: Topic[] = [];
      try {
        topics = (await getTopicsSubtopicsBySubject(subtask.subjectId)) as Topic[];
      } catch (apiError) {
          console.error('Error fetching topics/subtopics:', apiError);
          // Fallback: Try to get from available props
          if (subtask.topicId && Array.isArray(availableTopics)) {
            const topic = availableTopics.find(t => {
              const topicIdStr = String(t.topic_id || '').trim();
              const subtaskTopicIdStr = String(subtask.topicId || '').trim();
              return topicIdStr === subtaskTopicIdStr;
            });
            if (topic && topic.topic_name) {
              topicName = String(topic.topic_name).trim();
              if (subtask.subtopicId && Array.isArray(availableSubtopics)) {
                const subtopic = availableSubtopics.find(st => {
                  const stIdStr = String(st.subtopic_id || '').trim();
                  const subtaskSubtopicIdStr = String(subtask.subtopicId || '').trim();
                  return stIdStr === subtaskSubtopicIdStr;
                });
                if (subtopic && subtopic.subtopic_name) {
                  subtopicName = String(subtopic.subtopic_name).trim();
                }
              }
            }
          }
      }
      
      // Step 3: Find topic and subtopic names from topics data
      if (topics.length > 0 && subtask.topicId && subtask.subtopicId) {
        const topic = topics.find((t: Topic) => {
          const topicIdStr = String(t.topic_id || '').trim();
          const subtaskTopicIdStr = String(subtask.topicId || '').trim();
          return topicIdStr === subtaskTopicIdStr;
        });
        if (topic && topic.topic_name) {
          topicName = String(topic.topic_name).trim();
          if (topic.sub_topics && Array.isArray(topic.sub_topics) && subtask.subtopicId) {
            const subtopic = topic.sub_topics.find((st: Subtopic) => {
              const stIdStr = String(st.subtopic_id || '').trim();
              const subtaskSubtopicIdStr = String(subtask.subtopicId || '').trim();
              return stIdStr === subtaskSubtopicIdStr;
            });
            if (subtopic && subtopic.subtopic_name) {
              subtopicName = String(subtopic.subtopic_name).trim();
            }
          }
        }
      }
      
      // Fallback: If we still don't have names, try to get from available props
      if ((!topicName || !subtopicName) && subtask.topicId && Array.isArray(availableTopics)) {
        const topic = availableTopics.find(t => {
          const topicIdStr = String(t.topic_id || '').trim();
          const subtaskTopicIdStr = String(subtask.topicId || '').trim();
          return topicIdStr === subtaskTopicIdStr;
        });
        if (topic && topic.topic_name && !topicName) {
          topicName = String(topic.topic_name).trim();
        }
        if (subtask.subtopicId && !subtopicName && Array.isArray(availableSubtopics)) {
          const subtopic = availableSubtopics.find(st => {
            const stIdStr = String(st.subtopic_id || '').trim();
            const subtaskSubtopicIdStr = String(subtask.subtopicId || '').trim();
            return stIdStr === subtaskSubtopicIdStr;
          });
          if (subtopic && subtopic.subtopic_name) {
            subtopicName = String(subtopic.subtopic_name).trim();
          }
        }
      }
      
      // Set subtopic info
      setSubtopicInfo({
        subject_name: subjectName,
        topic_name: topicName,
        subtopic_name: subtopicName
      });
      
      // Step 4 & 5: Only fetch content list for MCQ/Coding types
      if (isMcqCoding) {
        const allQuestionIds: string[] = [];
        const l1 = (subtask as any).level1 || [];
        const l2 = (subtask as any).level2 || [];
        const l3 = (subtask as any).level3 || [];
        
        if (Array.isArray(l1)) allQuestionIds.push(...l1.map((id: any) => String(id)));
        if (Array.isArray(l2)) allQuestionIds.push(...l2.map((id: any) => String(id)));
        if (Array.isArray(l3)) allQuestionIds.push(...l3.map((id: any) => String(id)));
        
        // Step 5 & 6: Fetch content list with question IDs and store in state
        if (allQuestionIds.length > 0) {
          const payload: any = {
            subtopic_id: subtask.subtopicId,
            content_type: subtask.subtaskType,
            question_ids: allQuestionIds
          };
          if (subtask.subtaskType === "coding") {
            payload.editor = subtask.editor || null;
          }
          const contentResponse = await apiPost('api/admin/content/list', payload);
          setViewModeContent(contentResponse.data);
        }
      }
      
      setLoadingViewData(false);
    } catch (error) {
      console.error('Error fetching view mode data:', error);
      setLoadingViewData(false);
    }
  };
  
  /**
   * Fetches video preview data (otp and playback_info) from API
   * Step 1: Call student/videos/{video_id}/ API
   * Step 2: Check if otp and playback_info are empty strings
   * Step 3: If empty, call onPreviewContent with empty strings to show error message
   * Step 4: If valid, call onPreviewContent with the fetched data
   * @param videoId - The video ID to fetch preview data for
   */
  const handleVideoPreview = async (videoId: string) => {
    try {
      const response = await apiGet(`student/videos/${videoId}/`);
      if (response.data) {
        // Check if otp and playback_info are empty strings
        const hasEmptyData = (!response.data.otp || response.data.otp.trim() === "") || 
                            (!response.data.playback_info || response.data.playback_info.trim() === "");
        
        if (hasEmptyData) {
          // Pass empty strings to trigger error message in modal
          onPreviewContent("video", {
            type: "video",
            otp: "",
            playback_info: ""
          });
        } else {
          onPreviewContent("video", {
            type: "video",
            otp: response.data.otp,
            playback_info: response.data.playback_info
          });
        }
      }
    } catch (error) {
      console.error('Error fetching video preview:', error);
    }
  };

  /**
   * Fetches notes preview data (content) from API
   * Step 1: Call student/notes/{notes_id}/ API
   * Step 2: Check if content is empty string
   * Step 3: If empty, call onPreviewContent with empty string to show error message
   * Step 4: If valid, call onPreviewContent with the fetched data
   * @param notesId - The notes ID to fetch preview data for
   */
  const handleNotesPreview = async (notesId: string) => {
    try {
      const response = await apiGet(`student/notes/${notesId}/`);
      if (response.data) {
        // Check if content is empty string
        const hasEmptyData = !response.data.content || response.data.content.trim() === "";
        
        if (hasEmptyData) {
          // Pass empty string to trigger error message in modal
          onPreviewContent("notes", {
            type: "notes",
            content: ""
          });
        } else {
          onPreviewContent("notes", {
            type: "notes",
            content: response.data.content
          });
        }
      }
    } catch (error) {
      console.error('Error fetching notes preview:', error);
    }
  };

  /**
   * Effect: Fetches view mode data when component is expanded (view-only)
   * Condition: Fetches if expanded, has existing content, and subtopicId exists (for MCQ/Coding/Video/Notes)
   */
  useEffect(() => {
    if (isExpanded && hasExistingContent && subtask.subtopicId && (isMcqCoding || isVideoOrNotes)) {
      fetchViewModeData();
    }
  }, [isExpanded, isMcqCoding, isVideoOrNotes, hasExistingContent, subtask.subtopicId, subtask.subtaskType, subjects]);
  
  const showContentSection =
  subtask.showDropdowns &&
  subtask.actionType &&
  ["video", "notes", "mcq", "coding"].includes(subtask.subtaskType);

  /**
   * Initializes content selection from a specific level array (level1, level2, or level3)
   * Step 1: Get the data array for the specified level
   * Step 2: If data exists, convert IDs to comma-separated string
   * Step 3: Update the selected content level and IDs via callbacks
   * @param level - The level key ("level1", "level2", or "level3")
   * @returns true if content was initialized, false otherwise
   */
  /**
   * Effect: For non-MCQ/Coding types, fetch content via parent when expanded (view-only; no edit initialization)
   */
  useEffect(() => {
    if (!isExpanded || !subtask.subtopicId) return;
    const isMcqCoding = subtask.subtaskType === "mcq" || subtask.subtaskType === "coding";
    if (showContentSection && !isMcqCoding) {
    }
  }, [isExpanded, subtask.subtopicId, subtask.subtaskType, showContentSection]);

  /**
   * Retrieves content items for the currently selected level, filtered by subtask type
   * Step 1: Determine content source based on subtask type (mcqCodingContent vs availableContent)
   * Step 2: Filter videos by level if subtask type is "video"
   * Step 3: Filter files by level if subtask type is "notes"
   * Step 4: Filter questions by level if subtask type is "mcq" or "coding"
   * Step 5: Return normalized content array with consistent structure
   * @returns Array of content items with normalized properties
   */
  const getContentForLevel = (): Array<{ content_id: string; text: string; time: string; level: string; type: "video" | "notes" | "mcq" | "coding" | "project_coding"; playback_info?: string; otp?: string; content?: string }> => {
    const isMcqCoding = subtask.subtaskType === "mcq" || subtask.subtaskType === "coding";
    // Step 1: Determine content source
    const hasContent = isMcqCoding ? mcqCodingContent : subtask.availableContent;
    
    if (!hasContent || !subtask.selectedContentLevel) return [];
    
    const level = subtask.selectedContentLevel;
    const content: Array<{ content_id: string; text: string; time: string; level: string; type: "video" | "notes" | "mcq" | "coding" | "project_coding"; playback_info?: string; otp?: string; content?: string }> = [];
    
    // Step 2: Filter videos by level
    if (subtask.subtaskType === "video" && subtask.availableContent && subtask.availableContent.videos) {
      Object.values(subtask.availableContent.videos).forEach(video => {
        if (video.level === level) {
          content.push({
            ...video,
            content_id: String(video.content_id),
            type: "video"
          });
        }
      });
    }
    
    // Step 3: Filter files by level
    if (subtask.subtaskType === "notes" && subtask.availableContent && subtask.availableContent.files) {
      Object.values(subtask.availableContent.files).forEach(file => {
        if (file.level === level) {
          content.push({
            ...file,
            content_id: String(file.content_id),
            type: "notes"
          });
        }
      });
    }

    // Step 4: Filter questions by level for MCQ/Coding
    if (
      (subtask.subtaskType === "mcq" || subtask.subtaskType === "coding") &&
      mcqCodingContent
    ) {
      const levelKey = subtask.selectedContentLevel as string;
      const levelContent = mcqCodingContent.content[levelKey];
      
      if (Array.isArray(levelContent)) {
        levelContent.forEach((q: any) => {
          content.push({
            content_id: q.content_id,
            text: q.title,
            level: subtask.selectedContentLevel!,
            time: "",
            type: subtask.subtaskType as "video" | "notes" | "mcq" | "coding" | "project_coding"
          });
        });
      }
    }
    
    // Step 5: Return normalized content array
    return content;
  };

  const filteredContent = getContentForLevel();


  return (
    <div className="mb-2 border rounded p-2 bg-light">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2 flex-grow-1">
          
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
            style={{ minWidth: "28px" }}
          >
            <FiChevronDown
              size={16}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2" style={{ fontSize: "13px" }}>{getSubtaskTypeIcon(subtask.subtaskType as SubtaskTypes)} {subtask.id}</span>
          <span style={{ fontSize: "13px" }}>{subtask.name || "—"}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: "13px" }}>Mandatory: {subtask.isMandatory ? "Yes" : "No"}</span>
          
        </div>
      </div>
      <Collapse in={isExpanded}>
        <div>
          {/* Display-only: use single table for Subject, Topic, Sub Topic, Type, Editor */}
          {subtask.showDropdowns && (
            <div className="mt-3 pt-3 border-top">
              <SubtopicInfoTable
                subtopicInfo={subtopicInfo}
                typeLabel={SUBTASK_TYPES.find(t => t.type === subtask.subtaskType)?.label ?? subtask.subtaskType ?? undefined}
                editorLabel={subtask.subtaskType === "coding" && subtask.editor
                  ? (CODING_EDITOR_TYPES?.find(e => e.type === subtask.editor)?.label ?? subtask.editor ?? undefined)
                  : undefined}
              />
            </div>
          )}

          {/* Show content section (view-only) for MCQ/Coding/Video/Notes with existing content */}
        {(showContentSection || ((isMcqCoding || isVideoOrNotes) && hasExistingContent)) && (
          <div className="mt-4 pt-2 border-top">
              {/* View for MCQ/Coding with existing content */}
              {isMcqCoding && hasExistingContent && (
                <div>
                  {loadingViewData ? (
                    <div>Loading...</div>
                  ) : viewModeContent && viewModeContent.content ? (
                    <div>
                      {Object.keys(viewModeContent.content).map((levelKey) => {
                        const levelQuestions = viewModeContent.content[levelKey] || [];
                        return (
                          <LevelQuestionsDisplay 
                            key={levelKey}
                            levelKey={levelKey}
                            questions={levelQuestions}
                            subtask={subtask}
                            contentStatus={contentStatus || undefined}
                            onStatusClick={handleStatusClick}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted">No data available</div>
                  )}
                </div>
              )}
              
              {/* View for Video/Notes with existing content */}
              {isVideoOrNotes && hasExistingContent && subtask.selectedContentId && (
                <div>
                  {/* Video/Notes Display with Preview Button */}
                  <div className="d-flex justify-content-between mb-2 align-items-center">
                    <div className="d-flex align-items-center p-2 border rounded flex-grow-1">
                      <strong className="ms-2">
                        {subtask.subtaskType.toUpperCase()}
                      </strong>
                      {subtask.selectedContentLevel && (
                        <> - {subtask.selectedContentLevel} </>
                      )}
                      {" - "}
                      {subtask.selectedContentId}
                    </div>
                    <span className="d-flex align-items-center gap-2">
                      {subtask.selectedContentTime && (
                        <strong>{subtask.selectedContentTime || "0"}m</strong>
                      )}
                      <button
                        className="border border-none rounded-2 ms-2"
                        onClick={() => {
                          if (subtask.subtaskType === "video" && subtask.selectedContentId) {
                            handleVideoPreview(subtask.selectedContentId);
                          } else if (subtask.subtaskType === "notes" && subtask.selectedContentId) {
                            handleNotesPreview(subtask.selectedContentId);
                          }
                        }}
                        style={{ backgroundColor: "#F1F2FF", padding: "4px 12px" }}
                      >
                        Preview
                      </button>
                      {subtask.selectedContentId && (
                        <ContentStatusIndicator
                          contentId={subtask.selectedContentId}
                          contentType={subtask.subtaskType as "video" | "notes" | "mcq" | "coding" | "project_coding"}
                          contentStatus={contentStatus}
                          onStatusClick={handleStatusClick}
                        />
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default SubtaskComponent;